import { Paper } from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo, useState } from "react";
import { isMobile, isMobileSafari, isTablet } from "react-device-detect";
import settings from "../../app-settings.json";
import AppNotificationDialog from "../../components/app-notification-dialog/AppNotificationDialog";
import AppNotificationToast from "../../components/app-notification-toast/AppNotificationToast";
import InstallPwaDialog, { InstallPwaDialogResult } from "../../components/install-pwa-dialog/InstallPwaDialog";
import ServiceWorkerManager from "../../components/service-worker-manager/ServiceWorkerManager";
import UserActivityLogger from "../../components/user-activity-logger/UserActivityLogger";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { AppNotificationContextProvider } from "../../contexts/app-notification-context/AppNotificationContextProvider";
import { ConfirmationContextProvider } from "../../contexts/confirmation-context/ConfirmationContext";
import { OfflineDetectContextProvider } from "../../contexts/offline-detect-context/OfflineDetectContextProvider";
import { RouteHistoryContextProvider } from "../../contexts/route-history-context/RouteHistoryContextProvider";
import { useUserContext } from "../../contexts/user-context/UserContext";
import useInstallPwaPromptState from "../../hooks/use-install-pwa-prompt-state/useInstallPwaPromptState";
// import cacheService from "../../services/cache-service/CacheService";
import { Outlet } from "react-router";
import { UserFavoritesContextProvider } from "../../contexts/user-favorites-context/UserFavoritesContext";
import restService from "../../services/rest-service/RestService";
import { defaultPromptIntervalMinutes } from "../../app/App";
import storageService from "../../services/storage/storage-service/StorageService";
import { StorageKeys } from "../../services/storage/StorageKeys";

export function RootLayout() {
    // console.log("========== Rendering App ==========");

    const [appSettings,] = useAppSettings();
    const [userState] = useUserContext();
    const [initialized, setInitialized] = useState(false);
    const [
        showInstallPwaPrompt, install, postpone, disable
    ] = useInstallPwaPromptState(defaultPromptIntervalMinutes);

    const isPhone = isMobile && !isTablet;

    const theme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );

    useEffect(() => {
        if (!initialized) {

            const impersonatorUserID: string = storageService.session.get(StorageKeys.ImpersonatorUserID, '');
            const impersonatorUserLogin: string  = storageService.session.get(StorageKeys.ImpersonatorUserID, '');

            restService.setBaseUrl(settings.apiBaseUrl);
            restService.setHeaderParams({
                unitSetID: `${userState.unitSetID}`,
                language: `${userState.language}`,
                timezone: userState.timezone,
                impersonatingUserID: undefined,
                impersonatingUserLogin: undefined
            });

            if (impersonatorUserID !== '') {
                restService.setImpersonator(+impersonatorUserID, impersonatorUserLogin);
            }

            setInitialized(true);
        }

    }, [initialized, setInitialized, userState]);

    // console.log("RENDER - App");
    // const getUserConfirmation = async (message: string, callback: (ok: boolean) => void) => {
    //     const getUserConfirmation = cacheService.get(message);
    //     const result = await getUserConfirmation();

    //     callback(result);
    // };

    function onInstallPwaDialogClose(result: InstallPwaDialogResult) {
        if (result.reason === "install") {
            install();
        } else if (result.reason === "disable") {
            disable();
        } else if (result.reason === "postpone") {
            postpone();
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <ServiceWorkerManager />
            <Paper sx={{
                height: isMobileSafari && navigator.maxTouchPoints > 1 ? "-webkit-fill-available" : "100vh",
                //padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
                paddingLeft: 'env(safe-area-inset-left)',
                paddingTop: 'env(safe-area-inset-top)',
                paddingRight: 'env(safe-area-inset-right)',
                //paddingBottom: 'env(safe-area-inset-bottom)'
                paddingBottom: 0
            }}
                elevation={0}
            >
                <AppNotificationContextProvider>
                    <OfflineDetectContextProvider>
                        <ConfirmationContextProvider>
                            <RouteHistoryContextProvider>
                                {/* <UserFavoritesContextProvider> */}
                                    <Outlet />
                                {/* </UserFavoritesContextProvider> */}
                            </RouteHistoryContextProvider>
                        </ConfirmationContextProvider>
                        <AppNotificationDialog />
                        <AppNotificationToast />
                        <UserActivityLogger />

                        {isPhone &&
                            <InstallPwaDialog
                                open={showInstallPwaPrompt}
                                onClose={onInstallPwaDialogClose}
                                values={{
                                    title: "Install eLynx Mobile App",
                                    body: "For a better mobile experience, we recommend you install the eLynx Mobile App."
                                }} />}
                    </OfflineDetectContextProvider>
                </AppNotificationContextProvider>
            </Paper>
        </ThemeProvider>
    );
}
